import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { errorMessage } from "../lib/errors";
import { Download, Trash2, User as UserIcon, Share2, Globe, Linkedin, Copy, Eye, EyeOff } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

export default function Account() {
  const { user, logout, refreshMe } = useAuth();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [profile, setProfile] = useState({
    title: user.title || "",
    bio: user.bio || "",
    linkedin_url: user.linkedin_url || "",
    profile_public: user.profile_public || false,
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const exportData = async () => {
    const r = await api.get("/user/export-data");
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ecs-donnees-${user.email}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Vos données ont été exportées.");
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/user/me");
      toast.success("Votre compte est programmé pour suppression.");
      logout();
      nav("/");
    } catch { toast.error("Impossible de supprimer le compte."); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/user/profile", profile);
      await refreshMe();
      toast.success("Profil mis à jour !");
    } catch (err) { toast.error(errorMessage(err, "Impossible de sauvegarder")); }
    finally { setSavingProfile(false); }
  };

  const publicUrl = `${window.location.origin}/profil/${user.referral_code}`;
  const copyPublicUrl = () => { navigator.clipboard.writeText(publicUrl); toast.success("Lien copié !"); };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="account-page">
      <div className="text-xs uppercase tracking-widest text-gold-700 font-mono font-semibold mb-3">Compte</div>
      <h1 className="font-display text-4xl font-bold text-ink-900">Mes paramètres</h1>

      {/* Identity card */}
      <div className="surface rounded-2xl p-6 mt-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gold-50 border border-gold-200 flex items-center justify-center">
            <UserIcon className="w-6 h-6 text-gold-700" />
          </div>
          <div>
            <div className="font-display text-xl font-semibold text-ink-900">{user.full_name}</div>
            <div className="text-sm text-ink-500 font-mono">{user.email}</div>
          </div>
        </div>
        <div className="gold-divider my-4" />
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><div className="text-xs uppercase tracking-widest text-ink-500 font-semibold">Code parrainage</div><div className="font-mono text-gold-700 mt-1">{user.referral_code}</div></div>
          <div><div className="text-xs uppercase tracking-widest text-ink-500 font-semibold">Crédit disponible</div><div className="font-mono text-gold-700 mt-1">{user.credit_euros?.toFixed(2) || "0.00"}€</div></div>
        </div>
      </div>

      {/* Profile editor */}
      <form onSubmit={saveProfile} className="surface rounded-2xl p-6 mt-6" data-testid="profile-editor">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-display text-xl font-bold text-ink-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-gold-600" /> Profil public
          </h2>
          {profile.profile_public && (
            <Link to={`/profil/${user.referral_code}`} target="_blank" className="text-xs text-gold-700 hover:text-gold-800 inline-flex items-center gap-1" data-testid="view-public-profile">
              <Eye className="w-3.5 h-3.5" /> Voir mon profil
            </Link>
          )}
        </div>
        <p className="text-sm text-ink-600 mb-4">
          Créez un mini-CV public rassemblant vos certifications. Partageable par lien ou QR — idéal pour LinkedIn ou une candidature.
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-ink-700 mb-1.5 block">Titre professionnel</label>
            <input value={profile.title} onChange={(e)=>setProfile({...profile, title:e.target.value})}
              maxLength={100}
              placeholder="Ex : Conseillère bancaire · Passionnée d'art"
              className="w-full bg-white border border-ink-200 rounded-lg px-3 py-2.5 text-ink-900 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
              data-testid="profile-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-700 mb-1.5 block">Bio / mini-CV ({profile.bio.length}/800)</label>
            <textarea value={profile.bio} onChange={(e)=>setProfile({...profile, bio:e.target.value})}
              maxLength={800} rows={4}
              placeholder="Racontez-vous en quelques lignes : votre parcours, vos passions, ce que vous cherchez."
              className="w-full bg-white border border-ink-200 rounded-lg px-3 py-2.5 text-ink-900 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
              data-testid="profile-bio-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-700 mb-1.5 block flex items-center gap-1">
              <Linkedin className="w-3.5 h-3.5" /> URL LinkedIn (facultatif)
            </label>
            <input value={profile.linkedin_url} onChange={(e)=>setProfile({...profile, linkedin_url:e.target.value})}
              placeholder="linkedin.com/in/votre-nom"
              className="w-full bg-white border border-ink-200 rounded-lg px-3 py-2.5 text-ink-900 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
              data-testid="profile-linkedin-input" />
          </div>

          <label className="flex items-center gap-3 px-4 py-3 rounded-lg border border-ink-200 bg-ink-50 cursor-pointer">
            <input type="checkbox" checked={profile.profile_public} onChange={(e)=>setProfile({...profile, profile_public:e.target.checked})} data-testid="profile-public-toggle" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-ink-900 flex items-center gap-2">
                {profile.profile_public ? <Eye className="w-4 h-4 text-success" /> : <EyeOff className="w-4 h-4 text-ink-400" />}
                Rendre mon profil public
              </div>
              <div className="text-xs text-ink-500 mt-0.5">Toute personne avec le lien pourra voir votre nom, votre bio et vos certificats.</div>
            </div>
          </label>

          {profile.profile_public && (
            <div className="rounded-lg bg-gold-50 border border-gold-200 px-4 py-3 flex items-center gap-3 text-sm" data-testid="public-url-block">
              <Share2 className="w-4 h-4 text-gold-700 flex-shrink-0" />
              <span className="font-mono text-xs text-ink-800 flex-1 truncate">{publicUrl}</span>
              <button type="button" onClick={copyPublicUrl} className="text-gold-700 hover:text-gold-800" data-testid="copy-public-url">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}

          <button type="submit" disabled={savingProfile}
            className="px-5 py-2.5 rounded-lg bg-gold-500 text-white font-semibold hover:bg-gold-600 shadow-gold disabled:opacity-60"
            data-testid="save-profile-button">
            {savingProfile ? "Sauvegarde…" : "Enregistrer mon profil"}
          </button>
        </div>
      </form>

      {/* GDPR */}
      <div className="surface rounded-2xl p-6 mt-6">
        <h2 className="font-display text-xl font-bold text-ink-900 mb-2">Mes données personnelles (RGPD)</h2>
        <p className="text-sm text-ink-600 mb-4">Conformément au RGPD, vous pouvez à tout moment exporter vos données ou supprimer votre compte.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={exportData} className="px-5 py-2.5 rounded-lg border border-ink-200 hover:bg-ink-50 text-sm text-ink-800 font-medium inline-flex items-center gap-2" data-testid="export-data-button">
            <Download className="w-4 h-4" /> Télécharger mes données
          </button>
          <button onClick={() => setConfirm(true)} className="px-5 py-2.5 rounded-lg border border-danger/40 hover:bg-danger/10 text-danger text-sm font-medium inline-flex items-center gap-2" data-testid="delete-account-button">
            <Trash2 className="w-4 h-4" /> Supprimer mon compte
          </button>
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-6" data-testid="delete-confirm-modal">
          <div className="surface rounded-2xl p-8 max-w-md w-full">
            <h3 className="font-display text-xl font-bold text-ink-900">Confirmer la suppression</h3>
            <p className="text-sm text-ink-600 mt-2">
              Vos données seront définitivement purgées sous 72h. Cette action est irréversible.
              Vos certificats déjà délivrés resteront vérifiables publiquement par leur code.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setConfirm(false)} className="px-4 py-2 rounded-lg border border-ink-200 text-sm text-ink-700 hover:bg-ink-50" data-testid="delete-cancel">Annuler</button>
              <button onClick={deleteAccount} className="px-5 py-2 rounded-lg bg-danger text-white font-medium text-sm hover:bg-danger/90" data-testid="delete-confirm">Confirmer la suppression</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
